import { PbPropsProvider } from '@content/helpers/src/context'
import { getPBProps, makeId } from '@content/helpers/src/utils.ts'
import { useEffect, useRef, useState } from 'preact/hooks'
import styles from './FeaturedProducts.module.scss'
import { ModalProvider } from './ModalContext'
import { CategoryProps, PBProps } from './types'

import CategoriesChips from './components/categoriesChips/CategoriesChips'
import ProductModal from './components/productModal/ProductModal'
import ProductsSlider from './components/productsSlider/ProductsSlider'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker'
import SectionHeader from '@commons/section-header/SectionHeader'

type FeaturedProductsProps = {
    data: string
}

const modalId = `modal-${makeId(4)}`

const itemsWithCategories = (
    categories: CategoryProps[],
    activeCategory?: string
) => {
    let mergedItems: any[] = []
    const categoryStartIndexMap: Record<string, number> = {}

    categories.forEach(category => {
        const currentStartIndex = mergedItems.length
        categoryStartIndexMap[category.id] = currentStartIndex

        const categoryItemsWithMeta = category.items.map((item, index) => ({
            ...item,
            category: category.id,
            currentIndex: currentStartIndex + index
        }))

        mergedItems.push(...categoryItemsWithMeta)
    })

    if (activeCategory) {
        return {
            items: mergedItems,
            startIndex: categoryStartIndexMap[activeCategory] ?? 0,
            category: activeCategory
        }
    }

    const result = categories.map(category => ({
        items: mergedItems,
        startIndex: categoryStartIndexMap[category.id],
        category: category.id
    }))

    return result
}

const FeaturedProducts = ({ data }: FeaturedProductsProps) => {
    const pbProps = {
        ...getPBProps<PBProps>(data),
        modalId
    }

    const { copywriting, layout, campaign } = pbProps as PBProps
    const { categories } = copywriting
    const [activeCategory, setActiveCategory] = useState('original')
    const [activeCategoryData, setActiveCategoryData] = useState(
        itemsWithCategories(categories, activeCategory)
    )
    const layoutPadding = [layout.padding_top, layout.padding_bottom]
    const containerRef = useRef(null)
    const carouselRef = useRef(null)
    const [splideInstance, setSplideInstance] = useState(null)
    useImpressionTracker(containerRef, campaign)

    const handleCategoryChange = data => {
        setActiveCategory(prev => {
            if (prev !== data) return data
            return prev
        })
    }

    useEffect(() => {
        setActiveCategoryData(itemsWithCategories(categories, activeCategory))
    }, [activeCategory])

    useEffect(() => {
        if (!activeCategory) {
            setActiveCategory(categories[0]?.id as string)
        } else {
            carouselRef?.current?.api?.splide?.go(activeCategoryData.startIndex)
        }
    }, [categories, activeCategory])

    let debounceTimer: ReturnType<typeof setTimeout>

    const updateCategoryDebounced = (categoryId: string) => {
        clearTimeout(debounceTimer)

        debounceTimer = setTimeout(() => {
            setActiveCategory(prev => (prev !== categoryId ? categoryId : prev))
        }, 100)
    }
    useEffect(() => {
        if (!carouselRef?.current?.api?.splide && !splideInstance) return

        setSplideInstance(carouselRef.current.api.splide)

        if (!splideInstance) return
        const items = itemsWithCategories(categories)
        const handleMoved = () => {
            const currentIndex = splideInstance.Components.Controller.getIndex()

            const matched = items.find(item => item.startIndex === currentIndex)
            if (matched) updateCategoryDebounced(matched.category)
        }

        splideInstance.on('moved', handleMoved)

        return () => {
            splideInstance.off('moved', handleMoved)
        }
    }, [splideInstance, carouselRef?.current?.api?.splide])

    return (
        <div
            ref={containerRef}
            className={`${[...layoutPadding].join(' ')} ${styles.featuredProducts}`}>
            <PbPropsProvider value={pbProps}>
                <ModalProvider>
                    <header className={styles.featuredProductsHeader}>
                        <SectionHeader
                            headlineTag="h2"
                            alignMobile="left"
                            headline={copywriting.title}
                            subhead={copywriting.subtitle}
                            description={copywriting.description}
                        />
                    </header>

                    <CategoriesChips
                        categoryCallback={handleCategoryChange}
                        activeCategory={activeCategory}
                    />

                    <ProductsSlider
                        activeCategoryData={activeCategoryData}
                        ref={carouselRef}
                    />
                    <ProductModal categoryId={activeCategory} />
                </ModalProvider>
            </PbPropsProvider>
        </div>
    )
}

export default FeaturedProducts
